/* iter-120 — Single-source phone formatting helper.
 *
 * Accepts any reasonable phone-number input (E.164, NANP 10-digit,
 * loose freeform with separators) and returns a consistent display
 * string of the form:
 *
 *     +1 (876) 353-1698
 *
 * • NANP numbers (US, Canada, Jamaica, etc. — country code 1) always
 *   render as `+1 (XXX) XXX-XXXX`.
 * • Other international numbers fall back to a sensible grouping
 *   `+CC AAA BBB CCCC` while keeping the leading `+`.
 * • Strings that don't contain enough digits to be a real phone number
 *   are returned unchanged.
 */

function digitsOnly(s) {
  return (s || '').toString().replace(/[^\d]/g, '');
}

export function formatPhone(raw) {
  if (!raw) return '';
  const orig = String(raw).trim();
  if (!orig) return '';
  let digits = digitsOnly(orig);
  if (!digits) return orig;

  // Auto-prepend country-code 1 when we see a bare 10-digit NANP number.
  if (digits.length === 10) digits = '1' + digits;

  // NANP: country code 1 + 10 digits → +1 (XXX) XXX-XXXX
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const mid = digits.slice(4, 7);
    const end = digits.slice(7, 11);
    return `+1 (${area}) ${mid}-${end}`;
  }

  // Other international: split a 1-3 digit CC then group the rest in 3-3-…
  if (digits.length > 7) {
    // Heuristic country code length: try 1, 2, then 3 digits.
    let ccLen = 1;
    if (digits.length >= 12) ccLen = 3;
    else if (digits.length === 11) ccLen = 2;
    const cc = digits.slice(0, ccLen);
    const rest = digits.slice(ccLen);
    const groups = [];
    let i = 0;
    while (i < rest.length) {
      const next = Math.min(rest.length - i, i === 0 ? 3 : (rest.length - i <= 4 ? rest.length - i : 3));
      groups.push(rest.slice(i, i + next));
      i += next;
    }
    return `+${cc} ${groups.join(' ')}`;
  }

  // Too short to be a real phone number — return as-is.
  return orig;
}

/** Returns the digits-only form, e.g. for tel: links and WhatsApp deep links. */
export function phoneDigits(raw) {
  return digitsOnly(raw);
}
